import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

// Inbox multi-select: which conversation rows are checked. Shared between the list
// (MessageCard.jsx — row checkboxes) and the bulk action bar (InboxBulkActionBar.jsx),
// which are separate components. Keyed by contactId (never by row index — the list is
// virtualized and reorders live), and capped at MAX_BULK_SELECTION to match the
// server's per-job cap (server/utils/inboxBulkActionQueue.js MAX_BULK_CONTACTS).
export const MAX_BULK_SELECTION = 50;

/**
 * Starts a bulk Close/Assign job (POST /api/inbox/bulk/:action). The server only validates and
 * queues it — the chats are processed in the background, one at a time.
 * Rejects with { status, code, message } so the bar can show the server's own message
 * (e.g. 409 BULK_ACTION_RUNNING).
 */
export const startInboxBulkAction = createAsyncThunk(
  "inboxBulkSelection/start",
  async ({ action, contactIds, targetId, targetType, targetName }, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) return rejectWithValue({ status: 401, message: "Your session has expired. Please log in again." });
      const body = action === "assign" ? { contactIds, targetId, targetType } : { contactIds };
      const { data } = await axios.post(`${SERVER_DOMAIN}/api/inbox/bulk/${action}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { jobId: data.jobId, action: data.action, total: data.total, targetName: targetName || null };
    } catch (err) {
      return rejectWithValue({
        status: err.response?.status || 0,
        code: err.response?.data?.code || null,
        message: err.response?.data?.message || "Couldn't start the bulk action. Please try again.",
      });
    }
  }
);

const inboxBulkSelectionSlice = createSlice({
  name: "inboxBulkSelection",
  initialState: {
    selectedIds: {},
    count: 0,
    // Selection mode without anything checked yet — entered from the "Select" button on
    // touch screens, which have no avatar hover to reveal the checkboxes.
    selectionMode: false,
    // contactIds of the selectable rows currently in the list (no groups), published by
    // MessageCard.jsx while selection is active — used by the bar's select-all checkbox.
    selectableIds: [],
    starting: false,
    // The job the current user started: { jobId, action, total, processed, state, targetName,
    // summary, results }. Progress/result handling is driven by the bar.
    activeJob: null,
  },
  reducers: {
    toggleBulkSelection: (state, action) => {
      const id = String(action.payload);
      if (state.selectedIds[id]) {
        delete state.selectedIds[id];
        state.count -= 1;
      } else if (state.count < MAX_BULK_SELECTION) {
        state.selectedIds[id] = true;
        state.count += 1;
      }
    },
    // Adds ids up to the cap (used by "select all loaded"); already-checked ids are kept.
    addToBulkSelection: (state, action) => {
      for (const raw of action.payload || []) {
        if (state.count >= MAX_BULK_SELECTION) break;
        const id = String(raw);
        if (!state.selectedIds[id]) {
          state.selectedIds[id] = true;
          state.count += 1;
        }
      }
    },
    // Drops ids that are no longer selectable (e.g. the row left the current tab).
    removeFromBulkSelection: (state, action) => {
      for (const raw of action.payload || []) {
        const id = String(raw);
        if (state.selectedIds[id]) {
          delete state.selectedIds[id];
          state.count -= 1;
        }
      }
    },
    clearBulkSelection: (state) => {
      if (state.count === 0 && !state.selectionMode) return;
      state.selectedIds = {};
      state.count = 0;
      state.selectionMode = false;
    },
    enterBulkSelectionMode: (state) => {
      state.selectionMode = true;
    },
    setBulkSelectableIds: (state, action) => {
      state.selectableIds = Array.isArray(action.payload) ? action.payload.map(String) : [];
    },
    // Restores a job started before a page refresh (InboxBulkJobTracker.jsx, sessionStorage).
    restoreActiveBulkJob: (state, action) => {
      if (!state.activeJob && action.payload?.jobId) state.activeJob = action.payload;
    },
    updateActiveBulkJob: (state, action) => {
      if (!state.activeJob || state.activeJob.jobId !== action.payload?.jobId) return;
      state.activeJob = { ...state.activeJob, ...action.payload };
    },
    clearActiveBulkJob: (state) => {
      state.activeJob = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startInboxBulkAction.pending, (state) => {
        state.starting = true;
      })
      .addCase(startInboxBulkAction.fulfilled, (state, action) => {
        state.starting = false;
        state.activeJob = { ...action.payload, processed: 0, state: "waiting", startedAt: Date.now(), summary: null, results: null };
        // The job now carries its own copy of the chats; the list goes back to normal.
        state.selectedIds = {};
        state.count = 0;
        state.selectionMode = false;
      })
      .addCase(startInboxBulkAction.rejected, (state) => {
        state.starting = false;
      });
  },
});

export const {
  toggleBulkSelection,
  addToBulkSelection,
  removeFromBulkSelection,
  clearBulkSelection,
  enterBulkSelectionMode,
  setBulkSelectableIds,
  restoreActiveBulkJob,
  updateActiveBulkJob,
  clearActiveBulkJob,
} = inboxBulkSelectionSlice.actions;

// A job that hasn't finished yet (BullMQ states before completed/failed).
export const isBulkJobRunning = (job) =>
  !!job && (job.state === "waiting" || job.state === "active" || job.state === "delayed" || job.state === "prioritized");

export const selectBulkSelectionCount = (state) => state.inboxBulkSelection.count;
export const selectBulkSelectedIdsMap = (state) => state.inboxBulkSelection.selectedIds;
export const selectIsBulkSelected = (contactId) => (state) =>
  state.inboxBulkSelection.selectedIds[String(contactId)] === true;
// Checkboxes visible on every row: something is checked, or selection mode was entered manually.
export const selectBulkSelectionActive = (state) =>
  state.inboxBulkSelection.count > 0 || state.inboxBulkSelection.selectionMode;
export const selectBulkSelectableIds = (state) => state.inboxBulkSelection.selectableIds;
export const selectBulkStarting = (state) => state.inboxBulkSelection.starting;
export const selectActiveBulkJob = (state) => state.inboxBulkSelection.activeJob;

export default inboxBulkSelectionSlice.reducer;
